import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Truck, PackageCheck, AlertTriangle, RotateCcw, CalendarClock, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/services/api';
import { formatDate, formatDateOnly } from '@/utils/dateFormat';
import { ORDER_STATUS, money } from './orderStatus';

const Tile = ({ icon: Icon, label, value, tone, testid, onClick }) => (
  <button type="button" onClick={onClick} className="text-left rounded-lg border bg-white px-3 py-2 hover:shadow-sm transition-shadow" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs text-gray-500"><Icon className="h-3.5 w-3.5" style={{ color: tone }} />{label}</div>
    <div className="text-xl font-bold mt-0.5" style={{ color: tone }}>{value}</div>
  </button>
);

export const OnlineOrdersWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/ecom-admin/alerts');
      setData(res.data);
    } catch (e) {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return null;
  const go = (status) => navigate(`/hq/ecommerce/orders${status ? `?status=${status}` : ''}`);

  return (
    <Card className="bg-white border-gray-200 shadow-sm mb-8" data-testid="online-orders-widget">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
          <ShoppingBag className="h-5 w-5 text-indigo-600" /> Online Orders
          {data.new_orders > 0 && <Badge className="bg-emerald-600 text-white animate-pulse" data-testid="new-orders-badge">{data.new_orders} new</Badge>}
        </CardTitle>
        <button type="button" onClick={() => go('')} className="text-sm text-indigo-600 flex items-center gap-1" data-testid="widget-view-all">Manage <ArrowRight className="h-4 w-4" /></button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
          <Tile icon={ShoppingBag} label="New (to confirm)" value={data.new_orders} tone="#059669" testid="tile-new" onClick={() => go('paid')} />
          <Tile icon={PackageCheck} label="To pack" value={data.to_pack} tone="#0284c7" testid="tile-pack" onClick={() => go('confirmed')} />
          <Tile icon={Truck} label="To book shipment" value={data.to_book} tone="#4f46e5" testid="tile-book" onClick={() => go('packed,processing')} />
          <Tile icon={CalendarClock} label="Pickups today" value={data.pickups_today} tone="#7c3aed" testid="tile-pickups" onClick={() => go('ready_to_ship')} />
          <Tile icon={Truck} label="In transit" value={data.in_transit} tone="#0891b2" testid="tile-transit" onClick={() => go('picked_up,shipped,in_transit,out_for_delivery')} />
          <Tile icon={data.delayed ? AlertTriangle : RotateCcw} label={data.delayed ? 'Delayed' : 'Returns pending'} value={data.delayed || data.pending_returns} tone={data.delayed ? '#dc2626' : '#ea580c'} testid="tile-delayed" onClick={() => go(data.delayed ? 'picked_up,shipped,in_transit,out_for_delivery' : 'return_requested')} />
        </div>
        <div className="divide-y border rounded-lg" data-testid="recent-online-orders">
          {data.recent.map((o) => {
            const st = ORDER_STATUS[o.status] || { label: o.status, tone: 'bg-gray-100 text-gray-700' };
            return (
              <button type="button" key={o.id} onClick={() => navigate(`/hq/ecommerce/orders?open=${o.id}`)}
                className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-gray-50 text-sm" data-testid={`recent-order-${o.order_number}`}>
                <div className="w-36 shrink-0"><div className="font-medium">{o.order_number}</div><div className="text-xs text-gray-500">{formatDate(o.created_at)}</div></div>
                <div className="flex-1 min-w-0"><div className="truncate">{o.customer_name} · {o.item_count} item{o.item_count === 1 ? '' : 's'}</div><div className="text-xs text-gray-500 truncate">{o.serving_store_name}{o.courier_name ? ` · ${o.courier_name}` : ''}{o.awb_code ? ` · ${o.awb_code}` : ''}</div></div>
                <div className="hidden md:block w-40 text-xs text-gray-600">
                  {o.pickup_scheduled_date && <div>Pickup {formatDate(o.pickup_scheduled_date)}</div>}
                  <div>ETA {o.expected_delivery_date ? `${formatDateOnly(o.expected_delivery_date)}${o.eta_is_estimate ? ' (est.)' : ''}` : '—'}</div>
                </div>
                <Badge className={`${st.tone} border-0 shrink-0`}>{st.label}</Badge>
                <div className="w-24 text-right font-semibold shrink-0">{money(o.grand_total)}</div>
              </button>
            );
          })}
          {data.recent.length === 0 && <div className="p-4 text-sm text-gray-500 text-center">No online orders yet</div>}
        </div>
      </CardContent>
    </Card>
  );
};
